import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import Button, { ButtonProps } from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ChevronDown, LucideIcon } from "lucide-react";
import { Divider } from "@mui/material";

export const StyledMenu = styled(
  ({
    anchorOrigin = { vertical: "bottom", horizontal: "left" },
    transformOrigin = { vertical: "top", horizontal: "left" },
    ...props
  }: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      {...props}
    />
  ),
)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    border: `1px solid ${theme.palette.gray[3]}`,
    marginTop: theme.spacing(1),
    color: "rgb(55, 65, 81)",
    boxShadow: theme.elevation[1],
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

type Option = {
  key?: string;
  title: string | React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  IconComponent?: LucideIcon;
  belowDivider?: boolean;
  disabled?: boolean;
  startIcon?: LucideIcon | React.ReactNode;
  endIcon?: LucideIcon | React.ReactNode;
};

interface Props extends ButtonProps {
  buttonContent: string | React.ReactNode;
  options: Option[];
  noIcon?: boolean;
  dropdownIcon?: boolean;
  variant?: "text" | "outlined" | "contained";
  anchorToCursor?: boolean;
  anchorOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  transformOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  keepOpenOnItemClick?: boolean;
  menuOffset?: string | number;
}

export default ({
  disabled,
  options,
  buttonContent,
  noIcon,
  dropdownIcon,
  variant = "outlined",
  anchorToCursor = false,
  anchorOrigin,
  transformOrigin,
  keepOpenOnItemClick = false,
  menuOffset,
  ...otherProps
}: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [cursorPos, setCursorPos] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const open = Boolean(anchorEl) || Boolean(cursorPos);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorToCursor) {
      setCursorPos({ top: event.clientY, left: event.clientX });
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      setCursorPos(null);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setCursorPos(null);
  };
  const fromOption = (option: Option) => (
    <MenuItem
      disabled={disabled || option.disabled}
      onClick={(e) => {
        option.onClick?.(e);
        if (!keepOpenOnItemClick) {
          handleClose();
        }
      }}
      disableRipple
      key={option.key || option.title?.toString()}
      sx={{ gap: "8px", padding: "8px 24px 8px 20px" }}
    >
      {option.startIcon}
      {!noIcon && option.IconComponent && (
        <option.IconComponent size={20} stroke={"#8E9598"} />
      )}

      {option.title}
      {option.endIcon}
    </MenuItem>
  );
  const topOptions = options.filter((option) => !option.belowDivider);
  const bottomOptions = options.filter((option) => option.belowDivider);
  return (
    <div>
      <Button
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant={variant}
        disableElevation
        onClick={handleClick}
        disabled={disabled}
        endIcon={
          dropdownIcon ? <ChevronDown size={20} stroke="#8E9598" /> : undefined
        }
        {...otherProps}
        sx={{
          ...otherProps.sx,
        }}
      >
        {buttonContent}
      </Button>
      <StyledMenu
        anchorReference={anchorToCursor ? "anchorPosition" : "anchorEl"}
        anchorEl={anchorEl}
        anchorPosition={
          cursorPos ? { top: cursorPos.top, left: cursorPos.left } : undefined
        }
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        open={open}
        onClose={handleClose}
        sx={menuOffset ? { marginTop: menuOffset } : undefined}
      >
        {topOptions.map(fromOption)}
        {bottomOptions.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {bottomOptions.map(fromOption)}
      </StyledMenu>
    </div>
  );
};
